import { CommonModule, DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { LoadingComponent } from '@components/shared/loading/loading.component';
import { ShipmentDeviceViewModel } from '@components/shipment-list/shipment-list-table/shipment-list-table.component';
import { ShipmentStatus } from '@components/shipment-list/shipment-status';
import { ShipmentRouteViewModel } from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';
import { ShipmentJudgedReasonType } from '@entities/shipment';
import { FilterService } from '@services/filter.service';
import { addMinutes, format, subDays, subMinutes } from 'date-fns';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { ShipmentRouteTableComponent } from '../shipment-route-table/shipment-route-table.component';

export type LatestAlertModel = {
  /** 配送ID */
  shipmentId: string;
  /** 配送アラート */
  status: string;
  /** 発生時刻 */
  eventAt: string;
};

/** 最新のアラート */
export type LatestSensorAlertModel = {
  type: string;
  eventAt?: string;
};

/** 配送毎のステータス一覧のViewModel */
export type ShipmentStatusViewModel = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送位置情報ID */
  locationId: string | null;
  /** 配送ステータス */
  status: string;
  /** 発生時間 */
  eventAt: string;
  /** 解除時間 */
  resolvedAt: string | null;
};

/** 配送設定のViewModel */
export type ShipmentViewModel = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送設定名 */
  shipmentName: string;
  /** センサーアラート取得開始時間(分数) */
  alertDetectionOffset?: number;
  /** 配送ステータス */
  status: string;
  /** 出発予定日時 */
  startDatetime: string;
  /** 出発実績日時 */
  actualStartDatetime?: string;
  /** 到着予定日時 */
  arrivalDatetime?: string;
  /** 到着実績日時 */
  actualArrivalDatetime?: string;
  /** ルート設定 */
  route: ShipmentRouteViewModel;
  /** デバイス一覧 */
  deviceList: ShipmentDeviceViewModel[];
  /** 最新の配送センサーアラート */
  latestSensorAlert?: LatestSensorAlertModel;
  /**最新の配送アラート */
  latestAlert?: LatestAlertModel;
  /** 配送ステータス履歴 */
  statusHistory?: ShipmentStatusViewModel[];
  /** センサーアラート取得開始実績時間 */
  sensorAlertStartTime?: string;
  /** 地図表示用の履歴取得開始時刻 */
  mapHistoryFrom?: string;
};

/** 配送情報一覧のフィルター */
interface ShipmentInfoListFilter {
  shipmentName?: string;
  shipmentStatus?: ShipmentStatus;
  alert?: string;
  shipmentRouteAndCurrentLocation?: string;
  deviceName?: string;
  sortState?: Sort;
  pageIndex?: number;
}

/** アラートステータス */
export enum AlertStatus {
  NotReady = 'NotReady',
  NotStarted = 'NotStarted',
  NotArrived = 'NotArrived',
  Alert = 'Alert',
  Warn = 'Warn',
}

/** 配送情報テーブルカラム */
export const SHIPMENT_INFORMATION_TABLE_COLUMN: string[] = [
  'number',
  'shipmentName',
  'status',
  'shipmentRouteAndCurrentLocation',
  'deviceName',
  'operation',
] as const;

@Component({
  selector: 'app-shipment-info-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatTooltipModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCardModule,
    RouterModule,
    DatePipe,
    DirectivesModule,
    ShipmentRouteTableComponent,
    LoadingComponent,
  ],
  templateUrl: './shipment-info-list-table.component.html',
  styleUrl: './shipment-info-list-table.component.scss',
})
export class ShipmentInfoListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** 配送設定一覧 */
  @Input({ required: true }) public shipmentList: ShipmentViewModel[] = [];

  /** ルートのローディング表示 */
  @Input() public routeLoading = false;

  /** 配送実績の対象期間 */
  @Input() public resultHistoryFrom = '';
  @Input() public resultHistoryTo = '';

  /** デフォルトフィルター値 */
  @Input() public defaultFilters: { [key: string]: string | boolean | number } =
    {};

  /** 編集が可能な権限か */
  @Input() public editableRole = false;

  /** センサーアラート取得開始時間を表示するか */
  @Input() public showSensorAlertStartTime = true;

  /** アラート地図表示 */
  @Output() public opened = new EventEmitter<ShipmentViewModel>();

  /** 準備完了 */
  @Output() public statusGottenReady = new EventEmitter<string>();

  /** 配送中断 */
  @Output() public statusInterrupted = new EventEmitter<string>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<ShipmentViewModel>([]);

  /** カラム */
  public displayedColumns: string[] = SHIPMENT_INFORMATION_TABLE_COLUMN;

  /** 配送ステータスリスト */
  public shipmentStatusList: string[] = ['0', '1'];

  /** 配送名キーワード */
  public shipmentNameKeyword = '';

  /**デバイス名キーワード */
  public deviceNameKeyword = '';

  /** 配送ステータス選択 */
  public selectedShipmentStatus: ShipmentStatus | undefined;

  /** 初期ソート状態 */
  public initSortState: Sort = {
    active: 'shipmentRouteAndCurrentLocation',
    direction: 'desc',
  };

  /** 重複なしの配送ステータスの論理名リスト */
  public uniqueStatusList: string[] = [];

  // 配送ステータスのenumプロパティに基づく画面表示名（論理名）対応表
  private shipmentStatusDescriptionList: { [key in ShipmentStatus]: string } = {
    [ShipmentStatus.Creating]: '作成中',
    [ShipmentStatus.Create]: '作成済',
    [ShipmentStatus.NotReady]: '出発準備未完',
    [ShipmentStatus.Ready]: '出発準備済',
    [ShipmentStatus.InProgress]: '配送中',
    [ShipmentStatus.AtStop]: '経由地到着',
    [ShipmentStatus.AtDestination]: '到着地到着',
    [ShipmentStatus.Completed]: '完了',
    [ShipmentStatus.Deleted]: '完了',
    [ShipmentStatus.Interrupted]: '中断',
  };

  // 配送ステータスのenumプロパティに基づく並び順対応表
  private shipmentStatusOrderList: { [key in ShipmentStatus]: number } = {
    [ShipmentStatus.Creating]: 0,
    [ShipmentStatus.Create]: 1,
    [ShipmentStatus.NotReady]: 2,
    [ShipmentStatus.Ready]: 3,
    [ShipmentStatus.InProgress]: 4,
    [ShipmentStatus.AtStop]: 5,
    [ShipmentStatus.AtDestination]: 6,
    [ShipmentStatus.Completed]: 7,
    [ShipmentStatus.Interrupted]: 8,
    [ShipmentStatus.Deleted]: 9,
  };

  // アラートステータスのenumプロパティに基づく並び順対応表
  private AlertStatusList: { [key in AlertStatus]: number } = {
    [AlertStatus.NotReady]: 1,
    [AlertStatus.NotStarted]: 2,
    [AlertStatus.NotArrived]: 3,
    [AlertStatus.Alert]: 4,
    [AlertStatus.Warn]: 5,
  };

  private readonly judgedReasonMessageMap: {
    [key in ShipmentJudgedReasonType]: string;
  } = {
    [ShipmentJudgedReasonType.DEVICE_NOT_AT_STARTING_POINT]:
      '出発地のジオフェンス内にデバイスが無いため、準備完了にできませんでした。',
    [ShipmentJudgedReasonType.UNEXPECTED_DEVICE_STATUS]:
      'デバイスステータスが「READY」でないため、準備完了にできませんでした。',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  constructor(
    private filterService: FilterService<
      ShipmentInfoListFilter,
      ShipmentViewModel
    >,
    private cdr: ChangeDetectorRef,
  ) {
    this.setupFilterPredicate();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (
      item: ShipmentViewModel,
      property: string,
    ) => {
      if (property === 'status') {
        return item.status ? this.getStatusOrder(item.status) : 0;
      }

      if (property === 'alert') {
        if (item.latestAlert) {
          return this.getAlertStatusOrder(item.latestAlert.status);
        } else if (item.latestSensorAlert) {
          return this.getAlertStatusOrder(item.latestSensorAlert.type);
        } else {
          return 10;
        }
      }

      if (property === 'deviceName') {
        return item.deviceList[0] ? item.deviceList[0].deviceName : '';
      }

      if (property === 'shipmentRouteAndCurrentLocation') {
        return item.actualStartDatetime ?? item.startDatetime ?? '';
      }

      const value = item[property as keyof ShipmentViewModel];
      return value == null ? '' : `${value}`;
    };

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルタ設定
  }

  ngOnInit(): void {
    // フィルターの値をデフォルト値に設定する
    for (const [key, value] of Object.entries(this.defaultFilters)) {
      if (key === 'shipmentName') {
        this.shipmentNameKeyword = `${value}`;
      }
    }

    this.dataSource.data = this.shipmentList;
    this.dataSource.sort = this.sort;
    this.applyFilter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shipmentList'] !== undefined) {
      const shipmentList: ShipmentViewModel[] =
        changes['shipmentList'].currentValue;
      this.dataSource.data = shipmentList;

      // 重複なしの配送ステータスの物理名リスト更新
      this.updateUniqueStatusList(shipmentList);
      this.applyFilter();
    }
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * 配送名でのフィルタリングをクリアする。
   */
  public clearShipmentNameFilter(): void {
    // フィルタ条件をクリア
    this.shipmentNameKeyword = '';

    this.applyFilter();
  }

  /**
   * 配送ステータスでのフィルタリングをクリアする。
   */
  public clearShipmentStatusFilter(): void {
    // フィルタ条件をクリア
    this.selectedShipmentStatus = undefined;

    this.applyFilter();
  }

  /**
   * デバイス名でのフィルタリングをクリアする。
   */
  public clearDeviceNameFilter(): void {
    // フィルタ条件をクリア
    this.deviceNameKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * デバイス詳細画面に遷移する際の条件に合ったクエリパラメータを取得する
   * @param element ShipmentViewModelの要素
   * @returns 日付検索開始日時
   */
  public getDeviceInfoUrlParams(element: ShipmentViewModel): {
    [key: string]: string;
  } {
    const queryParams: { [key: string]: string } = {
      page: 'shipment-info',
      shipmentId: element.shipmentId,
    };

    // 到着日時が指定されている場合、到着日時
    // 到着日時が指定されていない場合、デフォルト値として現在時刻を指定する
    const arrivalDatetime =
      element.arrivalDatetime || element.actualArrivalDatetime;
    let arrivalDate = new Date();
    if (arrivalDatetime) {
      arrivalDate = new Date(arrivalDatetime);
    }
    queryParams['historyTo'] = addMinutes(arrivalDate, 2).toISOString();

    // 地図表示の取得開始日時を取得して利用する
    if (element.mapHistoryFrom) {
      queryParams['historyFrom'] = element.mapHistoryFrom;
    } else {
      queryParams['historyFrom'] = subMinutes(
        subDays(arrivalDate, 1),
        2,
      ).toISOString();
    }

    if (this.resultHistoryFrom) {
      queryParams['resultHistoryFrom'] = this.resultHistoryFrom;
    }
    if (this.resultHistoryTo) {
      queryParams['resultHistoryTo'] = this.resultHistoryTo;
    }

    return queryParams;
  }

  /** 指定した日付(文字列型)が今日の日付になっているか真偽地で返す
   * @param startDatetime 指定した日付(文字列型)
   * @returns 今日の日付かどうか
   */
  public isToday(startDatetime: string): boolean {
    // startDatetimeをDateオブジェクトに変換
    const startDate = new Date(startDatetime);

    // 今日の日付を取得
    const today = new Date();

    // 年、月、日を比較
    return (
      startDate.getFullYear() === today.getFullYear() &&
      startDate.getMonth() === today.getMonth() &&
      startDate.getDate() === today.getDate()
    );
  }

  /**
   * 最新のエラーがアラートかワーニングか判定する
   * @param element テーブル行の要素
   * @returns
   */
  public isAlert(element: ShipmentViewModel): boolean {
    return element.latestSensorAlert?.type === 'Alert';
  }

  /**
   * アラート名の取得
   * @param alertsPerDevice デバイス毎のアラート
   * @returns アラートならTrue
   */
  public getAlertStatus(status: string): string {
    switch (status) {
      case 'NotArrived':
        return '着荷遅延';
      case 'NotStarted':
        return '出荷開始遅延';
      case 'NotReady':
        return '配送準備未完';
      default:
        return '';
    }
  }

  /**
   * アラートタイプを取得する
   * @param element テーブル行の要素
   * @returns
   */
  public getAlert(element: ShipmentViewModel): string {
    return element.latestAlert?.status || '';
  }

  /**
   * デバイス名を取得する
   * @param deviceList デバイス一覧
   * @returns カンマでつなげたデバイス名
   */
  public getDeviceNames(deviceList: ShipmentDeviceViewModel[]): string {
    const deviceNameList = deviceList.map((device) => device.deviceName);
    return deviceNameList.join(',');
  }

  /**
   * 配送ステータスの画面表示名（論理名）を取得する
   * @param status 配送ステータスの物理名
   * @returns 配送ステータスの画面表示名（論理名）
   */
  public getStatusDescription(status: string) {
    if (Object.values(ShipmentStatus).some((value) => value === status)) {
      return this.shipmentStatusDescriptionList[
        status as keyof typeof ShipmentStatus
      ];
    }
    return '';
  }

  /**
   * 配送ステータスの並び順を取得する
   * @param status 配送ステータスの物理名
   * @returns 配送ステータスの並び順
   */
  public getStatusOrder(status: string) {
    if (Object.values(ShipmentStatus).some((value) => value === status)) {
      return this.shipmentStatusOrderList[
        status as keyof typeof ShipmentStatus
      ];
    }
    return '';
  }

  /**
   * アラートステータスの並び順を取得する
   * @param status 配送ステータスの物理名
   * @returns 配送ステータスの並び順
   */
  public getAlertStatusOrder(status: string) {
    if (
      status &&
      Object.values(AlertStatus).some((value) => value === status)
    ) {
      return this.AlertStatusList[status as keyof typeof AlertStatus];
    }
    return '';
  }

  /** 配送設定一覧内に存在する配送ステータスのみの重複なしリスト更新
   * @param status 配送ステータスの物理名
   * @returns 配送ステータスの画面表示名（論理名）
   */
  public updateUniqueStatusList(shipmentList: ShipmentViewModel[]) {
    const uniqueStatusDescriptions = Array.from(
      new Set(
        shipmentList.map((shipment) => {
          return shipment.status;
        }),
      ),
    );
    this.uniqueStatusList = uniqueStatusDescriptions;
  }

  /**
   * センサーアラート取得開始予定時間を計算する関数
   * @param startDatetime 出発予定日時（ISO 8601形式の文字列）
   * @param alertDetectionOffset センサーアラート取得開始時間（単位は「分」）
   * @returns センサーアラート取得開始予定時間（'yyyy/MM/dd HH:mm'形式の文字列）
   */
  public calcAlertStartTime(
    startDatetime: string,
    alertDetectionOffset?: number,
  ): string {
    const startDate = new Date(startDatetime);
    // センサーアラート取得開始時間が設定されていない場合は0分として扱う
    const offsetMinutes = alertDetectionOffset ?? 0;
    const alertStartTime = subMinutes(startDate, offsetMinutes);

    return format(alertStartTime, 'yyyy/MM/dd HH:mm');
  }

  /**
   * 判定理由表示メッセージを取得する
   * @param judgedReason 判定理由
   * @returns 表示メッセージ
   */
  public getJudgedReasonMessage(
    judgedReason: ShipmentJudgedReasonType,
  ): string {
    return this.judgedReasonMessageMap[judgedReason] || '';
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.shipmentNameKeyword = filters.shipmentName || '';
    this.selectedShipmentStatus = filters.shipmentStatus;
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: ShipmentInfoListFilter = {
      shipmentName: this.shipmentNameKeyword,
      shipmentStatus: this.selectedShipmentStatus,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }

  /**
   * テーブルのフィルタ条件を設定する
   */
  private setupFilterPredicate(): void {
    this.dataSource.filterPredicate = (element: ShipmentViewModel) => {
      let shipmentName = true;
      if (this.shipmentNameKeyword) {
        shipmentName =
          element.shipmentName
            .trim()
            .toLowerCase()
            .indexOf(this.shipmentNameKeyword.toLowerCase()) > -1;
      }
      let shipmentStatus = true;
      if (this.selectedShipmentStatus) {
        shipmentStatus = element.status === this.selectedShipmentStatus;
      }
      let deviceName = true;
      if (this.deviceNameKeyword) {
        deviceName = element.deviceList.some((device) => {
          return (
            ((device.deviceName || device.deviceId) ?? '')
              .trim()
              .toLowerCase()
              .indexOf(this.deviceNameKeyword.toLowerCase()) > -1
          );
        });
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return shipmentName && shipmentStatus && deviceName;
    };
  }
}
